/* eslint-disable react/no-danger */
import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Collapse, Button, Tooltip, Select, Input, message } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faFileCode, faLink, faDownload } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector } from "react-redux";
// INTERFACES
import {
  AdapterBacnetInterface,
  BacnetBankMapping,
  BacnetConfigEntryInterface,
  BacnetLiftMapping,
  BacnetLn2Mapping,
} from "../../../../../interfaces/Adapter.interface";
import { RootState } from "../../../../../app/store";
// COMPONENTS
import FormInputText from "../../../../components/form/FormInputText";
import FormInputNumber from "../../../../components/form/FormInputNumber";
import FormSelectBank from "../../../../components/form/FormSelectBank";
import DrawerRowDivider from "../../../../components/drawer/DrawerRowDivider";
import FormSelectMessage from "../../../../components/form/FormSelectMessage";
import FormInputBooleanSwitch from "../../../../components/form/FormInputBooleanSwitch";
import SelectBanks from "../../../../components/select/SelectBanks";
import SelectLifts from "../../../../components/select/SelectLifts";
// CONSTANTS
import {
  DEFAULT_BACNET_LN2_MAPPING,
  DEFAULT_BACNET_BANK_MAPPING,
  DEFAULT_BACNET_LIFT_MAPPING,
} from "../../../../../constants/adapter";
// UUID UTILS
import { UuidT } from "../../../../../flatbuffers/generated/front-end/uuid";
import { stringToUuidT, uuidToString, uuidsEqual } from "../../../../../utils/uuid";
import { LiftType } from "../../../../../flatbuffers/generated/archive/lift-type";
import { Ln2MessageFilter } from "../../../../../flatbuffers/types/ln2MessageOptions";

// ============================================================
// Serialization helpers (exported for use in parent form)
// ============================================================

/** Convert BacnetBankMapping[] (UuidT) → plain objects (string UUIDs) for JSON persistence */
export function serializeMappings(mappings: BacnetBankMapping[]): object[] {
  return mappings.map((bm) => ({
    bacnet_bank_id: uuidToString(bm.bacnet_bank_id),
    bacnet_bank_name: bm.bacnet_bank_name,
    ln2_bank_id: uuidToString(bm.ln2_bank_id),
    ln2_bank_name: bm.ln2_bank_name,
    lift_mappings: bm.lift_mappings.map((lm) => ({
      bacnet_lift_id: uuidToString(lm.bacnet_lift_id),
      bacnet_lift_name: lm.bacnet_lift_name,
      ln2_lift_id: uuidToString(lm.ln2_lift_id),
      ln2_lift_name: lm.ln2_lift_name,
    })),
  }));
}

/** Convert raw JSON (string UUIDs or already UuidT) → BacnetBankMapping[] with UuidT fields */
export function deserializeMappings(raw: any[]): BacnetBankMapping[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((bm: any) => ({
    bacnet_bank_id: toUuidT(bm.bacnet_bank_id),
    bacnet_bank_name: bm.bacnet_bank_name || "",
    ln2_bank_id: toUuidT(bm.ln2_bank_id),
    ln2_bank_name: bm.ln2_bank_name || "",
    lift_mappings: Array.isArray(bm.lift_mappings)
      ? bm.lift_mappings.map((lm: any) => ({
          bacnet_lift_id: toUuidT(lm.bacnet_lift_id),
          bacnet_lift_name: lm.bacnet_lift_name || "",
          ln2_lift_id: toUuidT(lm.ln2_lift_id),
          ln2_lift_name: lm.ln2_lift_name || "",
        }))
      : [],
  }));
}

/**
 * Remap stale ln2_bank_id / ln2_lift_id values in a deserialized mapping array.
 *
 * When an org is re-imported the bank and lift UUIDs change but names stay the
 * same. This function detects IDs that are no longer in the current org and
 * rewrites them using the name-to-ID maps built from the wizard's bank/lift data.
 *
 * Returns the updated mappings plus a `bankIdMap` (old → new UUID string) so
 * the caller can rewrite matching ent.bank_id values in bacnet_config entries.
 */
export function remapStaleMappings(
  mappings: BacnetBankMapping[],
  currentBankIds: string[],
  currentBankNames: Record<string, string>,
  currentLiftIdsByBank: Record<string, string[]>,
  currentLiftNamesByBank: Record<string, Record<string, string>>,
): { remapped: BacnetBankMapping[]; bankIdMap: Record<string, string> } {
  const bankIdSet = new Set(currentBankIds);

  // Build name → new ID lookup for banks
  const bankNameToNewId: Record<string, string> = {};
  Object.entries(currentBankNames).forEach(([id, name]) => {
    bankNameToNewId[name] = id;
  });

  const bankIdMap: Record<string, string> = {};

  const remapped = mappings.map((bm) => {
    const oldBankId = uuidToString(bm.ln2_bank_id);
    if (bankIdSet.has(oldBankId)) {
      // Already current — no remapping needed
      return bm;
    }

    const newBankId = bm.ln2_bank_name ? bankNameToNewId[bm.ln2_bank_name] : undefined;
    if (!newBankId) {
      // No name match — leave as-is
      return bm;
    }

    bankIdMap[oldBankId] = newBankId;

    // Build name → new lift ID lookup for this bank
    const currentLiftIds = currentLiftIdsByBank[newBankId] || [];
    const currentLiftSet = new Set(currentLiftIds);
    const liftNames = currentLiftNamesByBank[newBankId] || {};
    const liftNameToNewId: Record<string, string> = {};
    Object.entries(liftNames).forEach(([id, name]) => {
      liftNameToNewId[name] = id;
    });

    const remappedLifts = bm.lift_mappings.map((lm) => {
      const oldLiftId = uuidToString(lm.ln2_lift_id);
      if (currentLiftSet.has(oldLiftId)) return lm;
      const newLiftId = lm.ln2_lift_name ? liftNameToNewId[lm.ln2_lift_name] : undefined;
      if (!newLiftId) return lm;
      return { ...lm, ln2_lift_id: stringToUuidT(newLiftId) };
    });

    return {
      ...bm,
      ln2_bank_id: stringToUuidT(newBankId),
      lift_mappings: remappedLifts,
    };
  });

  return { remapped, bankIdMap };
}

/**
 * Convert a value to UuidT — handles:
 * - UUID strings ("xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx")
 * - Numeric strings (interpreted as u128)
 * - Existing UuidT objects
 * - null/undefined
 */
/** Convert a BigInt to a UuidT by formatting as a 32-char hex string and using stringToUuidT */
function bigintToUuidT(num: bigint): UuidT {
  const hex = num.toString(16).padStart(32, "0");
  const uuid = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
    16,
    20,
  )}-${hex.slice(20, 32)}`;
  return stringToUuidT(uuid);
}

function toUuidT(val: any): UuidT {
  if (!val) return stringToUuidT("");
  if (typeof val === "string") {
    // If it looks like a UUID (contains dashes and is hex), use standard conversion
    const stripped = val.replace(/-/g, "");
    if (stripped.length === 32 && /^[0-9a-fA-F]+$/.test(stripped)) {
      return stringToUuidT(val);
    }
    // Try as a numeric string → u128
    try {
      return bigintToUuidT(BigInt(val));
    } catch {
      // Not a valid number either — treat as empty
      return stringToUuidT("");
    }
  }
  if (typeof val === "number" || typeof val === "bigint") {
    return bigintToUuidT(BigInt(val));
  }
  // Already a UuidT-like object
  if (val.value && (val.value.upper !== undefined || val.value.lower !== undefined)) {
    return val as UuidT;
  }
  return stringToUuidT("");
}

/**
 * Normalize a single ln2_msg from JSON.
 * The JSON stores a ValuedMessage: { msg_id, position, active }
 * The UI expands it to a FrontEndMessage: { bank_id, lift_id, msg: { ... } }
 * bank_id/lift_id are derived from the Bank/Lift Mapping, not the JSON.
 */
export function normalizeLn2Msg(raw: any): BacnetLn2Mapping["ln2_msg"] {
  if (raw && raw.msg && typeof raw.msg === "object") {
    // Already in nested format — keep msg, ignore bank_id/lift_id
    return { bank_id: "", lift_id: "", msg: raw.msg };
  }
  // Flat ValuedMessage format — wrap into nested
  return {
    bank_id: "",
    lift_id: "",
    msg: {
      encoded_type: raw?.encoded_type ?? 0,
      message_content: raw?.message_content ?? 0,
      message_value: raw?.message_value ?? 0,
      fbs_device_type: raw?.fbs_device_type ?? LiftType.ELEVATOR,
      msg_id: raw?.msg_id || "",
      position: raw?.position ?? 1,
      active: raw?.active ?? true,
    },
  };
}

/** Look up LN2 bank/lift IDs from mappings given BACnet bank/lift IDs */
export function lookupLn2FromMapping(
  bankMappings: BacnetBankMapping[],
  bacnetBankId: UuidT,
  bacnetLiftId: UuidT,
): { ln2BankId: string; ln2LiftId: string } | null {
  const bankMapping = bankMappings.find((bm) => uuidsEqual(bm.bacnet_bank_id, bacnetBankId));
  if (!bankMapping) return null;
  const liftMapping = bankMapping.lift_mappings.find((lm) =>
    uuidsEqual(lm.bacnet_lift_id, bacnetLiftId),
  );
  if (!liftMapping) return null;
  return {
    ln2BankId: uuidToString(bankMapping.ln2_bank_id),
    ln2LiftId: uuidToString(liftMapping.ln2_lift_id),
  };
}

/** Normalize all descriptions within a bacnet_config array so ln2_msg matches the interface.
 *  Resolves ln2_msg.bank_id and lift_id from the BACnet-to-LN2 bank/lift mapping. */
export function normalizeConfig(
  config: any[],
  mappings: BacnetBankMapping[],
): AdapterBacnetInterface[] {
  return config.map((item: any) => ({
    ...item,
    entries: (item.entries || []).map((ent: any) => ({
      ...ent,
      bank: ent.bank || {},
      bank_id: ent.bank_id || "",
      descriptions: (ent.descriptions || []).map((desc: any) => {
        const ln2Msg = normalizeLn2Msg(desc.ln2_msg);
        if (!ln2Msg.bank_id || !ln2Msg.lift_id) {
          const bankIdRaw = desc.point?.bank_id;
          const liftIdRaw = desc.point?.lift_id;
          const bacnetBankUuid = stringToUuidT(typeof bankIdRaw === "string" ? bankIdRaw : "");
          const bacnetLiftUuid = stringToUuidT(typeof liftIdRaw === "string" ? liftIdRaw : "");
          const lookup = lookupLn2FromMapping(mappings, bacnetBankUuid, bacnetLiftUuid);
          if (lookup) {
            ln2Msg.bank_id = lookup.ln2BankId;
            ln2Msg.lift_id = lookup.ln2LiftId;
          }
        }
        return { point: desc.point || {}, ln2_msg: ln2Msg };
      }),
    })),
  }));
}

export default function CdoBacnetPage({
  setCdo,
  cdoValue,
  bankMappings,
  setBankMappings,
  messageFilter = undefined,
}: {
  setCdo: React.Dispatch<React.SetStateAction<AdapterBacnetInterface[]>>;
  cdoValue: AdapterBacnetInterface[];
  bankMappings: BacnetBankMapping[];
  setBankMappings: React.Dispatch<React.SetStateAction<BacnetBankMapping[]>>;
  messageFilter?: Ln2MessageFilter;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { theme } = useSelector((state: RootState) => state.home);
  const { Panel } = Collapse;
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [description, setDescription] = useState<BacnetLn2Mapping>({
    ...DEFAULT_BACNET_LN2_MAPPING,
  });
  const [entry, setEntry] = useState<BacnetConfigEntryInterface>({
    bank: {},
    descriptions: [],
    bank_id: "",
  });
  const [entries, setEntries] = useState<AdapterBacnetInterface>({
    entries: [],
  });
  // Local string state for BACnet ID text inputs (free-form editing, converted to UuidT on blur/enter)
  const [bacnetBankIdDrafts, setBacnetBankIdDrafts] = useState<Record<number, string>>({});
  const [bacnetLiftIdDrafts, setBacnetLiftIdDrafts] = useState<Record<string, string>>({});

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const formatJsonWithColors = (obj: any) => {
    const json = JSON.stringify(obj, null, 4)
      .replace(/"([^"]+)"\s*:/g, '<span style="color:#9cdcfe;">"$1"</span>:')
      .replace(/: "([^"]*)"/g, ': <span style="color:#ce9178;">"$1"</span>')
      .replace(/:\s*(\d+)/g, ': <span style="color:#b5cea8;">$1</span>');
    return json;
  };

  // -- Deep copy that handles UuidT (BigInt) safely --
  const cloneMappings = (prev: BacnetBankMapping[]): BacnetBankMapping[] =>
    prev.map((bm) => ({
      ...bm,
      lift_mappings: bm.lift_mappings.map((lm) => ({ ...lm })),
    }));

  // -- Bank/Lift Mapping Handlers --
  const onAddBankMapping = () => {
    setBankMappings((prev) => [...prev, { ...DEFAULT_BACNET_BANK_MAPPING, lift_mappings: [] }]);
  };

  const onRemoveBankMapping = (index: number) => {
    setBankMappings((prev) => prev.filter((_, i) => i !== index));
    setBacnetBankIdDrafts({});
    setBacnetLiftIdDrafts({});
  };

  const onChangeBacnetBankId = (bankIndex: number, id: string) => {
    setBacnetBankIdDrafts((prev) => ({ ...prev, [bankIndex]: id }));
  };

  const commitBacnetBankId = (bankIndex: number) => {
    const raw = bacnetBankIdDrafts[bankIndex];
    if (raw === undefined) return;
    setBankMappings((prev) => {
      const copy = cloneMappings(prev);
      copy[bankIndex].bacnet_bank_id = toUuidT(raw);
      return copy;
    });
  };

  const onChangeBacnetBankName = (bankIndex: number, name: string) => {
    setBankMappings((prev) => {
      const copy = cloneMappings(prev);
      copy[bankIndex].bacnet_bank_name = name;
      return copy;
    });
  };

  const onChangeLn2Bank = (bankIndex: number, id: string, name: string) => {
    setBankMappings((prev) => {
      const copy = cloneMappings(prev);
      copy[bankIndex].ln2_bank_id = stringToUuidT(id);
      copy[bankIndex].ln2_bank_name = name;
      return copy;
    });
  };

  const onAddLiftMapping = (bankIndex: number) => {
    setBankMappings((prev) => {
      const copy = cloneMappings(prev);
      copy[bankIndex].lift_mappings.push({ ...DEFAULT_BACNET_LIFT_MAPPING });
      return copy;
    });
  };

  const onRemoveLiftMapping = (bankIndex: number, liftIndex: number) => {
    setBankMappings((prev) => {
      const copy = cloneMappings(prev);
      copy[bankIndex].lift_mappings = copy[bankIndex].lift_mappings.filter(
        (_: any, i: number) => i !== liftIndex,
      );
      return copy;
    });
    setBacnetLiftIdDrafts({});
  };

  const onChangeBacnetLiftId = (bankIndex: number, liftIndex: number, id: string) => {
    setBacnetLiftIdDrafts((prev) => ({
      ...prev,
      [`${bankIndex}-${liftIndex}`]: id,
    }));
  };

  const commitBacnetLiftId = (bankIndex: number, liftIndex: number) => {
    const key = `${bankIndex}-${liftIndex}`;
    const raw = bacnetLiftIdDrafts[key];
    if (raw === undefined) return;
    setBankMappings((prev) => {
      const copy = cloneMappings(prev);
      copy[bankIndex].lift_mappings[liftIndex].bacnet_lift_id = toUuidT(raw);
      return copy;
    });
  };

  const onChangeBacnetLiftName = (bankIndex: number, liftIndex: number, name: string) => {
    setBankMappings((prev) => {
      const copy = cloneMappings(prev);
      copy[bankIndex].lift_mappings[liftIndex].bacnet_lift_name = name;
      return copy;
    });
  };

  const onChangeLn2Lift = (bankIndex: number, liftIndex: number, id: string, name: string) => {
    setBankMappings((prev) => {
      const copy = cloneMappings(prev);
      copy[bankIndex].lift_mappings[liftIndex].ln2_lift_id = stringToUuidT(id);
      copy[bankIndex].lift_mappings[liftIndex].ln2_lift_name = name;
      return copy;
    });
  };

  // -- BACnet Point bank/lift handlers (from mapping dropdowns) --
  const inputOnChangePointBank = (id: string) => {
    const bm = bankMappings.find((m) => uuidToString(m.bacnet_bank_id) === id);
    setDescription((prev: BacnetLn2Mapping) => ({
      ...prev,
      point: {
        ...prev.point,
        bank_id: id,
        bank_name: bm?.bacnet_bank_name || "",
        lift_id: "",
        lift_name: "",
      },
    }));
  };

  const inputOnChangePointLift = (id: string) => {
    const bm = bankMappings.find(
      (m) => uuidToString(m.bacnet_bank_id) === description.point.bank_id,
    );
    const lm = bm?.lift_mappings.find(
      (l: BacnetLiftMapping) => uuidToString(l.bacnet_lift_id) === id,
    );
    setDescription((prev: BacnetLn2Mapping) => ({
      ...prev,
      point: {
        ...prev.point,
        lift_id: id,
        lift_name: lm?.bacnet_lift_name || "",
      },
    }));
  };

  const onChangeFormValueDescription = (
    newValue: string | number | boolean,
    variableName: string,
  ) => {
    const copy: BacnetLn2Mapping = structuredClone(description);
    if (variableName === "host_name" && typeof newValue === "string") {
      copy.point.host_name = newValue;
    } else if (variableName === "point_name" && typeof newValue === "string") {
      copy.point.point_name = newValue;
    } else if (variableName === "group_name" && typeof newValue === "string") {
      copy.point.group_name = newValue;
    } else if (variableName === "host_id" && typeof newValue === "string") {
      copy.point.host_id = newValue;
    } else if (variableName === "group_id" && typeof newValue === "string") {
      copy.point.group_id = newValue;
    } else if (variableName === "value" && typeof newValue === "number") {
      copy.point.value = newValue;
    } else if (variableName === "point_id" && typeof newValue === "number") {
      copy.point.point_id = newValue;
    } else if (variableName === "msg_id" && typeof newValue === "string") {
      copy.ln2_msg.msg.msg_id = newValue;
    } else if (variableName === "active" && typeof newValue === "boolean") {
      copy.ln2_msg.msg.active = newValue;
    } else if (variableName === "position" && typeof newValue === "number") {
      copy.ln2_msg.msg.position = newValue;
    }
    setDescription(copy);
  };

  const onClickAddNewDescription = () => {
    // Auto-populate LN2 bank/lift from mapping
    const bacnetBankUuid = stringToUuidT(description.point.bank_id);
    const bacnetLiftUuid = stringToUuidT(description.point.lift_id);
    const lookup = lookupLn2FromMapping(bankMappings, bacnetBankUuid, bacnetLiftUuid);
    if (!lookup) {
      message.error(
        "No mapping found for the selected BACnet Bank/Lift. Please add a mapping first.",
      );
      return;
    }
    const newDescription: BacnetLn2Mapping = {
      ...description,
      ln2_msg: {
        bank_id: lookup.ln2BankId,
        lift_id: lookup.ln2LiftId,
        msg: { ...description.ln2_msg.msg },
      },
    };
    setEntry((prev: BacnetConfigEntryInterface) => ({
      ...prev,
      descriptions: [...prev.descriptions, newDescription],
    }));
    setDescription({ ...DEFAULT_BACNET_LN2_MAPPING });
  };

  const onChangeBank = (id: string, name: string) => {
    setEntry((prev: BacnetConfigEntryInterface) => ({
      ...prev,
      bank_id: id,
      bank: { ...prev.bank, name },
    }));
  };

  const onClickAddNewEntry = () => {
    setEntries((prev: AdapterBacnetInterface) => ({
      entries: [...prev.entries, entry],
    }));
    setEntry({ bank: {}, descriptions: [], bank_id: "" });
  };

  const onClickSaveCdo = () => {
    setCdo((prev: AdapterBacnetInterface[]) => [...prev, entries]);
  };

  const onClickRemoveDescription = (index: number) => {
    setEntry((prev: BacnetConfigEntryInterface) => ({
      ...prev,
      descriptions: prev.descriptions.filter((_, i) => i !== index),
    }));
  };

  function uploadJSONFile(onLoadCallback: (data: any, fileName: string) => void) {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json";
    input.onchange = (event: any) => {
      const file = event.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text: any = e.target?.result;
          const data = JSON.parse(text);
          onLoadCallback(data, file.name);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        } finally {
          input.value = "";
        }
      };

      reader.readAsText(file);
    };

    input.click();
  }

  const onClickUploadJsonFile = () => {
    uploadJSONFile((data) => {
      // If the file has a top-level bacnet_bank_mappings field, load both
      if (
        data &&
        typeof data === "object" &&
        !Array.isArray(data) &&
        "bacnet_bank_mappings" in data
      ) {
        let parsedMappings: BacnetBankMapping[] = [];
        if (Array.isArray(data.bacnet_bank_mappings)) {
          parsedMappings = deserializeMappings(data.bacnet_bank_mappings);
          setBankMappings(parsedMappings);
          setBacnetBankIdDrafts({});
          setBacnetLiftIdDrafts({});
        }
        // The CDO entries can be in a "bacnet_config" field or "entries" field
        let configData: AdapterBacnetInterface[] = [];
        if (Array.isArray(data.bacnet_config)) {
          configData = normalizeConfig(data.bacnet_config, parsedMappings);
        } else if (Array.isArray(data.entries)) {
          configData = normalizeConfig([{ entries: data.entries }], parsedMappings);
        }
        setCdo(configData);

        // Populate local form state so the Formulary section shows the uploaded data
        if (configData.length > 0) {
          setEntries(configData[0]);
          const firstEntry = configData[0].entries?.[0];
          if (firstEntry) {
            setEntry(firstEntry);
          }
        }
      } else {
        // Legacy format: the file IS the bacnet_config array directly
        setCdo(data);
      }
      setDescription({ ...DEFAULT_BACNET_LN2_MAPPING });
    });
  };

  const onClickDownloadJsonFile = () => {
    const output = {
      bacnet_bank_mappings: serializeMappings(bankMappings),
      bacnet_config: cdoValue,
    };
    const blob = new Blob([JSON.stringify(output, null, 4)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bacnet-config.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Build dropdown options from mappings
  const bacnetBankOptions = bankMappings.map((bm) => ({
    value: uuidToString(bm.bacnet_bank_id),
    label: bm.bacnet_bank_name || uuidToString(bm.bacnet_bank_id),
  }));

  const selectedBankMapping = bankMappings.find(
    (bm) => uuidToString(bm.bacnet_bank_id) === description.point.bank_id,
  );
  const bacnetLiftOptions = selectedBankMapping
    ? selectedBankMapping.lift_mappings.map((lm: BacnetLiftMapping) => ({
        value: uuidToString(lm.bacnet_lift_id),
        label: lm.bacnet_lift_name || uuidToString(lm.bacnet_lift_id),
      }))
    : [];

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <DrawerRowDivider icon={faFileCode} title="BACNET" />
      {/* ======== JSON Preview ======== */}
      <Collapse>
        <Panel header={t("general.bacnetJsonPreview")} key="1">
          <div className="container">
            <div className={`drawer__box__${theme} row`}>
              <pre
                style={{
                  width: "100%",
                  color: "#dcdcdc",
                  padding: "10px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  overflowX: "auto",
                }}
                dangerouslySetInnerHTML={{
                  __html: formatJsonWithColors(cdoValue),
                }}
              />
            </div>
          </div>

          <div className="generalStyles__width100">
            <div>
              <Button onClick={onClickSaveCdo} className="buttonStyle__28 generalStyles__width100">
                <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                Add Entry
              </Button>
            </div>
          </div>
        </Panel>
      </Collapse>
      <br />
      {/* ======== Bank/Lift Mapping Section ======== */}
      <Collapse>
        <Panel
          header={
            <>
              <FontAwesomeIcon icon={faLink} className="generalStyles__mrFix" />
              {t("general.bacnetBankLiftMapping")}
            </>
          }
          key="1"
        >
          {bankMappings.map((bm: BacnetBankMapping, bankIdx: number) => (
            <div key={bankIdx} className="container mb-3">
              <div className={`drawer__box__${theme} row`} style={{ padding: "10px" }}>
                <div className="col-md-12">
                  <span className="drawer__text" style={{ fontWeight: "bold" }}>
                    Bank Mapping #{bankIdx + 1}
                  </span>
                  <Tooltip title={t("general.delete")}>
                    <Button
                      onClick={() => onRemoveBankMapping(bankIdx)}
                      className="buttonStyle__29"
                      shape="circle"
                      size="small"
                      style={{ float: "right" }}
                      icon={<FontAwesomeIcon icon={faTrash} />}
                    />
                  </Tooltip>
                </div>
                {/* BACnet Bank ID (arbitrary identifier, not an LN2 entity) */}
                <div className="col-md-12 mt-3">
                  <div className="generalStyles__flex">
                    <div className="generalStyles__inputFlexNameLarge">
                      <span className="generalStyles__inputFlexRequired">*</span>
                      <span className="drawer__text">BACnet Bank ID</span>
                    </div>
                    <div className="generalStyles__width100">
                      <Input
                        size="middle"
                        value={bacnetBankIdDrafts[bankIdx] ?? uuidToString(bm.bacnet_bank_id)}
                        placeholder="BACnet Bank ID (UUID or number)"
                        onChange={(e) => onChangeBacnetBankId(bankIdx, e.target.value)}
                        onBlur={() => commitBacnetBankId(bankIdx)}
                        onPressEnter={() => commitBacnetBankId(bankIdx)}
                      />
                    </div>
                    <div className="generalStyles__infoTooltipIconBlank" />
                  </div>
                </div>
                {/* BACnet Bank Name */}
                <div className="col-md-12 mt-3">
                  <div className="generalStyles__flex">
                    <div className="generalStyles__inputFlexNameLarge">
                      <span className="drawer__text">BACnet Bank Name</span>
                    </div>
                    <div className="generalStyles__width100">
                      <Input
                        size="middle"
                        value={bm.bacnet_bank_name}
                        placeholder="BACnet Bank Name (label)"
                        onChange={(e) => onChangeBacnetBankName(bankIdx, e.target.value)}
                      />
                    </div>
                    <div className="generalStyles__infoTooltipIconBlank" />
                  </div>
                </div>
                {/* LN2 Bank */}
                <div className="col-md-12 mt-3">
                  <div className="generalStyles__flex">
                    <div className="generalStyles__inputFlexNameLarge">
                      <span className="generalStyles__inputFlexRequired">*</span>
                      <span className="drawer__text">LN2 Bank</span>
                    </div>
                    <div className="generalStyles__width100">
                      <SelectBanks
                        inputValue={uuidToString(bm.ln2_bank_id)}
                        inputOnChange={(id: string, name: string) =>
                          onChangeLn2Bank(bankIdx, id, name)
                        }
                        disabledInput={false}
                      />
                    </div>
                    <div className="generalStyles__infoTooltipIconBlank" />
                  </div>
                </div>
                {/* Lift Mappings */}
                <div className="col-md-12 mt-3">
                  <span className="drawer__text" style={{ fontWeight: "bold", fontSize: "12px" }}>
                    Lift Mappings
                  </span>
                </div>
                {bm.lift_mappings.map((lm: BacnetLiftMapping, liftIdx: number) => (
                  <div
                    key={liftIdx}
                    className="col-md-12 mt-2"
                    style={{
                      paddingLeft: "20px",
                      borderLeft: "2px solid #333",
                    }}
                  >
                    <div className="generalStyles__flex">
                      <div className="generalStyles__inputFlexNameLarge">
                        <span className="drawer__text">BACnet Lift ID</span>
                      </div>
                      <div className="generalStyles__width100">
                        <Input
                          size="middle"
                          value={
                            bacnetLiftIdDrafts[`${bankIdx}-${liftIdx}`] ??
                            uuidToString(lm.bacnet_lift_id)
                          }
                          placeholder="BACnet Lift ID (UUID or number)"
                          onChange={(e) => onChangeBacnetLiftId(bankIdx, liftIdx, e.target.value)}
                          onBlur={() => commitBacnetLiftId(bankIdx, liftIdx)}
                          onPressEnter={() => commitBacnetLiftId(bankIdx, liftIdx)}
                        />
                      </div>
                      <div className="generalStyles__infoTooltipIconBlank" />
                    </div>
                    <div className="generalStyles__flex mt-2">
                      <div className="generalStyles__inputFlexNameLarge">
                        <span className="drawer__text">BACnet Lift Name</span>
                      </div>
                      <div className="generalStyles__width100">
                        <Input
                          size="middle"
                          value={lm.bacnet_lift_name}
                          placeholder="BACnet Lift Name (label)"
                          onChange={(e) => onChangeBacnetLiftName(bankIdx, liftIdx, e.target.value)}
                        />
                      </div>
                      <div className="generalStyles__infoTooltipIconBlank" />
                    </div>
                    <div className="generalStyles__flex mt-2">
                      <div className="generalStyles__inputFlexNameLarge">
                        <span className="drawer__text">LN2 Lift</span>
                      </div>
                      <div className="generalStyles__width100">
                        <SelectLifts
                          inputValue={uuidToString(lm.ln2_lift_id)}
                          inputOnChange={(id: string, name: string) =>
                            onChangeLn2Lift(bankIdx, liftIdx, id, name)
                          }
                          disabledInput={false}
                          bankId={uuidToString(bm.ln2_bank_id)}
                        />
                      </div>
                      <Tooltip title={t("general.delete")}>
                        <Button
                          onClick={() => onRemoveLiftMapping(bankIdx, liftIdx)}
                          className="buttonStyle__29"
                          shape="circle"
                          size="small"
                          icon={<FontAwesomeIcon icon={faTrash} />}
                        />
                      </Tooltip>
                    </div>
                  </div>
                ))}
                <div className="col-md-12 mt-2">
                  <Button
                    onClick={() => onAddLiftMapping(bankIdx)}
                    className="buttonStyle__28"
                    size="small"
                  >
                    <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                    Add Lift Mapping
                  </Button>
                </div>
              </div>
            </div>
          ))}
          <div className="container">
            <Button onClick={onAddBankMapping} className="buttonStyle__28 generalStyles__width100">
              <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
              Add Bank Mapping
            </Button>
          </div>
        </Panel>
      </Collapse>
      <br />
      {/* ======== Formulary ======== */}
      <Collapse>
        <Panel header={t("general.bacnetFormulary")} key="1">
          <div className="container">
            <div className={`drawer__box__${theme} row`}>
              <FormSelectBank
                inputValue={entry.bank_id}
                inputOnChange={onChangeBank}
                disabledInput={false}
              />
              <div className="col-md-12 mt-3">
                <div className="generalStyles__flex">
                  <div className="generalStyles__inputFlexNameLarge">
                    <span className="generalStyles__inputFlexRequired">*</span>
                    <span className="drawer__text">{t("general.descriptions")}</span>
                  </div>
                  <div className="generalStyles__width100">
                    {entry.descriptions.map((item: BacnetLn2Mapping, itemIndex: number) => (
                      <div
                        key={itemIndex}
                        className="mb-3"
                        style={{
                          backgroundColor: "#1f1f1f",
                          borderRadius: "5px",
                          fontSize: "9px",
                          padding: "10px",
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                          }}
                        >
                          <pre
                            style={{
                              width: "100%",
                              color: "#dcdcdc",
                              padding: "10px",
                              borderRadius: "8px",
                              overflowX: "auto",
                            }}
                            dangerouslySetInnerHTML={{
                              __html: formatJsonWithColors(item),
                            }}
                          />
                          <div className="generalStyles__flexCenter">
                            <Tooltip title={t("general.delete")}>
                              <Button
                                onClick={() => {
                                  onClickRemoveDescription(itemIndex);
                                }}
                                className="buttonStyle__29"
                                shape="circle"
                                icon={<FontAwesomeIcon icon={faTrash} />}
                              />
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="generalStyles__infoTooltipIconBlank" />
                </div>
              </div>
              <div className="col-md-12 mt-3">
                <div className="generalStyles__flex">
                  <div className="generalStyles__inputFlexNameLarge">
                    <span className="generalStyles__inputFlexRequired" />
                  </div>
                  <div className="generalStyles__width100">
                    <div>
                      <Button
                        onClick={onClickAddNewEntry}
                        className="buttonStyle__28 generalStyles__width100"
                      >
                        <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                        {t("general.add")}
                      </Button>
                    </div>
                  </div>
                  <div className="generalStyles__infoTooltipIconBlank" />
                </div>
              </div>
            </div>
          </div>
          <br />
          {/* ======== BACnet Point Description ======== */}
          <div className="container">
            <div className={`drawer__box__${theme} row`}>
              <div className="col-md-12">
                <span className="drawer__text" style={{ fontWeight: "bold" }}>
                  BACnet Point
                </span>
              </div>
              <FormInputText
                inputValue={description.point.host_name}
                inputLabelName="Host name"
                inputOnChange={onChangeFormValueDescription}
                inputVariableName="host_name"
                isRequired
                disabledInput={false}
              />
              <FormInputText
                inputValue={description.point.host_id}
                inputLabelName="Host ID"
                inputOnChange={onChangeFormValueDescription}
                inputVariableName="host_id"
                isRequired
                disabledInput={false}
              />
              {/* BACnet Bank — from mapping */}
              <div className="col-md-12 mt-3">
                <div className="generalStyles__flex">
                  <div className="generalStyles__inputFlexNameLarge">
                    <span className="generalStyles__inputFlexRequired">*</span>
                    <span className="drawer__text">BACnet Bank</span>
                  </div>
                  <div className="generalStyles__width100">
                    <Select
                      size="middle"
                      className="generalStyles__width100"
                      value={description.point.bank_id || undefined}
                      placeholder="Select BACnet Bank (from mapping)"
                      onChange={inputOnChangePointBank}
                      options={bacnetBankOptions}
                      showSearch
                      optionFilterProp="label"
                    />
                  </div>
                  <div className="generalStyles__infoTooltipIconBlank" />
                </div>
              </div>
              {/* BACnet Lift — from mapping */}
              <div className="col-md-12 mt-3">
                <div className="generalStyles__flex">
                  <div className="generalStyles__inputFlexNameLarge">
                    <span className="generalStyles__inputFlexRequired">*</span>
                    <span className="drawer__text">BACnet Lift</span>
                  </div>
                  <div className="generalStyles__width100">
                    <Select
                      size="middle"
                      className="generalStyles__width100"
                      value={description.point.lift_id || undefined}
                      placeholder="Select BACnet Lift (from mapping)"
                      onChange={inputOnChangePointLift}
                      options={bacnetLiftOptions}
                      showSearch
                      optionFilterProp="label"
                      disabled={!description.point.bank_id}
                    />
                  </div>
                  <div className="generalStyles__infoTooltipIconBlank" />
                </div>
              </div>
              <FormInputText
                inputValue={description.point.group_name}
                inputLabelName="Group name"
                inputOnChange={onChangeFormValueDescription}
                inputVariableName="group_name"
                isRequired
                disabledInput={false}
              />
              <FormInputText
                inputValue={description.point.group_id}
                inputLabelName="Group ID"
                inputOnChange={onChangeFormValueDescription}
                inputVariableName="group_id"
                isRequired
                disabledInput={false}
              />
              <FormInputText
                inputValue={description.point.point_name}
                inputLabelName="Point name"
                inputOnChange={onChangeFormValueDescription}
                inputVariableName="point_name"
                isRequired
                disabledInput={false}
              />
              <FormInputNumber
                inputValue={description.point.point_id}
                inputLabelName="Point ID"
                inputOnChange={onChangeFormValueDescription}
                inputVariableName="point_id"
              />
              <FormInputNumber
                inputValue={description.point.value}
                inputLabelName={t("general.value")}
                inputOnChange={onChangeFormValueDescription}
                inputVariableName="value"
              />
            </div>
          </div>
          <br />
          {/* ======== LN2 Valued Message ======== */}
          <div className="container">
            <div className={`drawer__box__${theme} row`}>
              <div className="col-md-12">
                <span className="drawer__text" style={{ fontWeight: "bold" }}>
                  LN2 Valued Message
                </span>
              </div>
              <FormSelectMessage
                inputValue={description.ln2_msg.msg.msg_id ?? ""}
                inputOnChange={onChangeFormValueDescription}
                disabledInput={false}
                displaySelect
                messageFilter={messageFilter}
              />
              <FormInputNumber
                inputValue={description.ln2_msg.msg.position ?? 0}
                inputOnChange={onChangeFormValueDescription}
                inputLabelName={`LN2 ${t("general.position")}`}
                inputVariableName="position"
              />
              <FormInputBooleanSwitch
                value={description.ln2_msg.msg.active}
                onChangeSwitch={onChangeFormValueDescription}
                labelName={`LN2 ${t("general.active")}`}
                variableName="active"
                isRequired
                disabledInput={false}
              />
              <div className="col-md-12 mt-3">
                <div className="generalStyles__flex">
                  <div className="generalStyles__inputFlexNameLarge">
                    <span className="generalStyles__inputFlexRequired" />
                  </div>
                  <div className="generalStyles__width100">
                    <div>
                      <Button
                        onClick={onClickAddNewDescription}
                        className="buttonStyle__28 generalStyles__width100"
                      >
                        <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                        {t("general.add")}
                      </Button>
                    </div>
                    <div className="mt-2">
                      <Button
                        onClick={onClickUploadJsonFile}
                        className="buttonStyle__28 generalStyles__width100"
                      >
                        <FontAwesomeIcon icon={faFileCode} className="generalStyles__mrFix" />
                        {t("general.uploadJson")}
                      </Button>
                    </div>
                    <div className="mt-2">
                      <Button
                        onClick={onClickDownloadJsonFile}
                        className="buttonStyle__28 generalStyles__width100"
                      >
                        <FontAwesomeIcon icon={faDownload} className="generalStyles__mrFix" />
                        {t("general.downloadJsonFile")}
                      </Button>
                    </div>
                  </div>
                  <div className="generalStyles__infoTooltipIconBlank" />
                </div>
              </div>
            </div>
          </div>
        </Panel>
      </Collapse>
      <br />
    </div>
  );
}
